import { FiBookOpen } from 'react-icons/fi';

export const essentialWordsData = {
  courseId: 'essential-words',
  courseType: 'vocabulary',
  title: 'Essential Words',
  icon: FiBookOpen,
  overview: {
    impact: 'Build a strong vocabulary foundation',
    focus: 'High-frequency words for IELTS',
    uses: 'All IELTS sections',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Why Essential Words Matter',
      content: `<p>A strong vocabulary is the foundation of IELTS success. These essential words appear frequently across all test sections.</p>`,
      points: [
        'Improve reading comprehension speed',
        'Express ideas more precisely in writing',
        'Understand listening passages better',
        'Sound more fluent in speaking',
      ],
    },
    {
      title: 'Academic Word List Categories',
      content: `<p>The Academic Word List (AWL) contains 570 word families essential for academic English:</p>`,
      table: {
        headers: ['Category', 'Example Words'],
        rows: [
          ['Analysis', 'analyze, examine, investigate, evaluate'],
          ['Research', 'data, evidence, findings, methodology'],
          ['Change', 'alter, modify, transform, adapt'],
          ['Comparison', 'similar, contrast, differ, distinguish'],
          ['Cause & Effect', 'consequently, therefore, result, impact'],
        ],
      },
    },
    {
      title: 'Learning Strategies',
      content: `<p>Effective vocabulary learning requires systematic practice:</p>`,
      points: [
        '<strong>Context learning:</strong> Learn words in sentences, not isolation',
        '<strong>Word families:</strong> Learn analyze, analysis, analytical together',
        '<strong>Collocations:</strong> Learn which words go together',
        '<strong>Active use:</strong> Use new words in your own sentences',
        '<strong>Spaced repetition:</strong> Review words at increasing intervals',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Meanings',
      type: 'select',
      instructions: 'Choose the correct meaning for each word.',
      questions: [
        {
          text: 'Significant',
          options: ['Important or meaningful', 'Small or minor', 'Unclear'],
          answer: 'Important or meaningful',
          explanation: '"Significant" means having importance or meaning.',
        },
        {
          text: 'Analyze',
          options: ['Examine in detail', 'Ignore completely', 'Summarize briefly'],
          answer: 'Examine in detail',
          explanation: '"Analyze" means to examine something carefully.',
        },
        {
          text: 'Evidence',
          options: ['Facts supporting a conclusion', 'Personal opinion', 'Guess'],
          answer: 'Facts supporting a conclusion',
          explanation: '"Evidence" refers to facts that support a point.',
        },
        {
          text: 'Consequently',
          options: ['As a result', 'Before that', 'In contrast'],
          answer: 'As a result',
          explanation: '"Consequently" introduces a result or effect.',
        },
        {
          text: 'Perceive',
          options: ['See or understand', 'Ignore', 'Forget'],
          answer: 'See or understand',
          explanation: '"Perceive" means to become aware of or understand something.',
        },
        {
          text: 'Substantial',
          options: ['Large or significant', 'Tiny', 'Temporary'],
          answer: 'Large or significant',
          explanation: '"Substantial" means considerable in importance, size, or worth.',
        },
      ],
    },
    {
      title: 'Word Families',
      type: 'select',
      instructions: 'Choose the correct word form for each sentence.',
      questions: [
        {
          text: 'The ___ of the data revealed interesting patterns. (analyze)',
          options: ['analysis', 'analyze', 'analytical'],
          answer: 'analysis',
          explanation: 'We need a noun here. "Analysis" is the noun form.',
        },
        {
          text: 'Her ___ to the team was invaluable. (contribute)',
          options: ['contribution', 'contribute', 'contributory'],
          answer: 'contribution',
          explanation: 'We need a noun. "Contribution" is the noun form of "contribute".',
        },
        {
          text: 'The results are ___ significant. (statistic)',
          options: ['statistically', 'statistical', 'statistics'],
          answer: 'statistically',
          explanation: 'We need an adverb to modify "significant". "Statistically" is correct.',
        },
        {
          text: 'The ___ effects of the policy are unclear. (economy)',
          options: ['economic', 'economical', 'economy'],
          answer: 'economic',
          explanation: '"Economic" means related to the economy (adjective form).',
        },
      ],
    },
    {
      title: 'Complete the Collocation',
      type: 'fill-in',
      instructions: 'Type the word that completes each academic collocation.',
      questions: [
        {
          text: 'The study ___ that the hypothesis was correct.',
          answer: 'demonstrated',
          explanation: '"Demonstrated" is a formal way to say "showed".',
        },
        {
          text: 'The data ___ a clear trend.',
          answer: 'reveals',
          explanation: '"Reveals" collocates with data in academic writing.',
        },
        {
          text: 'Further research is ___ to confirm these findings.',
          answer: 'required',
          explanation: '"Required" is more formal than "needed".',
        },
      ],
    },
    {
      title: 'Choose the Academic Synonym',
      type: 'select',
      instructions: 'Which word is more appropriate for academic/IELTS writing?',
      questions: [
        {
          text: 'Instead of "big", use:',
          options: ['substantial', 'huge', 'massive'],
          answer: 'substantial',
          explanation: '"Substantial" is the most academic choice.',
        },
        {
          text: 'Instead of "show", use:',
          options: ['demonstrate', 'prove', 'display'],
          answer: 'demonstrate',
          explanation: '"Demonstrate" is formal and commonly used in academic writing.',
        },
        {
          text: 'Instead of "get", use:',
          options: ['obtain', 'grab', 'receive'],
          answer: 'obtain',
          explanation: '"Obtain" is the most academic alternative to "get".',
        },
        {
          text: 'Instead of "help", use:',
          options: ['facilitate', 'aid', 'assist'],
          answer: 'facilitate',
          explanation: '"Facilitate" is formal and commonly used in academic contexts.',
        },
      ],
    },
    {
      title: 'Use in Context',
      type: 'writing',
      instructions: 'Write a sentence using each academic word.',
      questions: [
        {
          text: 'Use "significant" in a sentence about technology.',
          example: 'Technology has had a significant impact on the way people communicate.',
          tip: 'Remember: significant = important, meaningful, considerable.',
        },
        {
          text: 'Use "consequently" in a sentence about education.',
          example: 'Universities have increased tuition fees; consequently, many students face financial difficulties.',
          tip: '"Consequently" introduces a result—use after stating a cause.',
        },
        {
          text: 'Use "demonstrate" in a sentence about research.',
          example: 'Recent studies demonstrate a clear link between diet and health outcomes.',
          tip: '"Demonstrate" is more academic than "show".',
        },
      ],
    },
  ],
  tips: [
    'Learn 10 new words per day systematically',
    'Use vocabulary apps with spaced repetition',
    'Read academic texts to see words in context',
    'Keep a vocabulary notebook organized by topic',
  ],
  dailyPractice: [
    'Review previous words before learning new ones',
    'Write sentences using new vocabulary',
    'Find words in IELTS reading passages',
    'Use new words in speaking practice',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Essential Words: High-frequency academic vocabulary for IELTS including the Academic Word List, learning strategies, and systematic practice.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...essentialWordsData,
      title: essentialWordsData.title + ' (Foundations)',
      overview: {
        ...essentialWordsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: essentialWordsData.sections.slice(0, Math.max(2, Math.ceil(essentialWordsData.sections.length / 2))),
      exercises: essentialWordsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...essentialWordsData,
      title: essentialWordsData.title + ' (Advanced)',
      overview: {
        ...essentialWordsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: essentialWordsData.sections,
      exercises: essentialWordsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(essentialWordsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return essentialWordsData;
}
